import * as CopcLib from 'copc';
import { createLazPerf, type LazPerf } from 'laz-perf';
import { isEmpty, isNil } from 'lodash';
import type {
  BoundingBox3D,
  BoundsArray,
  CopcHeader,
  CopcReader,
  CopcType,
  LasHeader,
  Point,
  ProgressCallback,
  ViewType,
} from '../types';
import { checkAborted, getErrorMessage } from './helpers';

const lazPerfPromise = createLazPerf({
  locateFile: (path: string) => {
    if (path.endsWith('.wasm')) {
      return '/laz-perf.wasm';
    }
    return path;
  },
});

/**
 * Implementation of COPC file reader.
 * Handles low-level COPC file operations including initialization,
 * hierarchy traversal, and point data extraction.
 */
export class CopcReaderInstance implements CopcReader {
  private copcData: CopcType | null = null;
  private filename: string;
  private header: LasHeader | null = null;
  private lazPerf: LazPerf | null = null;

  constructor(filename: string) {
    this.filename = filename;
  }

  /**
   * Initializes the COPC file by loading header and metadata.
   * Must be called before any other operations.
   */
  async initialize(signal?: AbortSignal): Promise<void> {
    checkAborted(signal);
    this.lazPerf = await lazPerfPromise;
    this.copcData = await CopcLib.Copc.create(this.filename);
    checkAborted(signal);
    this.header = this.copcData.header;
  }

  /**
   * Gets header information from the COPC file including bounds and point count.
   */
  getHeader(): CopcHeader {
    if (isNil(this.header)) {
      throw new Error('COPC file not initialized');
    }

    const { min, max, pointCount } = this.header;

    return {
      min: { x: min[0], y: min[1], z: min[2] },
      max: { x: max[0], y: max[1], z: max[2] },
      pointCount,
    };
  }

  /**
   * Gets the octree cube bounds from COPC metadata.
   */
  getCube(): BoundsArray {
    if (isNil(this.copcData)) {
      throw new Error('COPC file not initialized');
    }
    return this.copcData.info.cube as BoundsArray;
  }

  /**
   * Gets the WKT (Well-Known Text) coordinate reference system string.
   */
  getWkt(): string | undefined {
    if (isNil(this.copcData)) {
      throw new Error('COPC file not initialized');
    }
    return this.copcData.wkt;
  }

  /**
   * Retrieves all points within a 3D bounding box.
   * Traverses the COPC octree hierarchy to find relevant nodes and extracts points.
   */
  async getPointsWithinBox(
    queryBbox: BoundingBox3D,
    signal?: AbortSignal,
    onNodeProgress?: ProgressCallback,
  ): Promise<Point[]> {
    checkAborted(signal);

    if (isNil(this.copcData) || isNil(this.header)) {
      throw new Error('COPC file not initialized');
    }

    const points: Point[] = [];
    const rootPage = this.copcData.info.rootHierarchyPage;
    const filteredNodes = new Map<string, CopcLib.Hierarchy.Node>();

    await this.loadNodesInBounds(rootPage, filteredNodes, queryBbox, signal);
    checkAborted(signal);

    const totalNodes = filteredNodes.size;
    let processedNodes = 0;

    // Parallelize node loading with browser concurrency limit.
    const MAX_CONCURRENT_NODES = 3;
    let activeLoads = 0;
    const nodeEntries = Array.from(filteredNodes.entries());

    const loadNextNodes = async (): Promise<void> => {
      while (
        nodeEntries.length > 0 &&
        activeLoads < MAX_CONCURRENT_NODES &&
        !signal?.aborted
      ) {
        const entry = nodeEntries.shift();
        if (!entry) break;

        const [key, node] = entry;
        activeLoads++;

        // Load node asynchronously.
        if (isNil(this.copcData)) {
          throw new Error('COPC file not initialized');
        }

        CopcLib.Copc.loadPointDataView(this.filename, this.copcData, node, {
          lazPerf: this.lazPerf,
        })
          .then((view) => {
            if (signal?.aborted) {
              return;
            }
            if (!isNil(view) && view.pointCount > 0) {
              const nodePoints = this.extractPointsFromView(view, queryBbox);
              if (!isEmpty(nodePoints)) {
                points.push(...nodePoints);
              }
            }
          })
          .catch((error: unknown) => {
            console.error(
              `Failed to load node ${key}: ${getErrorMessage(error)}`,
            );
          })
          .finally(() => {
            processedNodes++;
            if (!isNil(onNodeProgress) && totalNodes > 0) {
              onNodeProgress(processedNodes, totalNodes);
            }
            activeLoads--;
            loadNextNodes().catch((err) => {
              console.error('Error in loadNextNodes:', err);
            });
          });
      }
    };

    // Starting to load nodes.
    await loadNextNodes();

    // Waiting for all nodes to complete processing.
    while (processedNodes < totalNodes && !signal?.aborted) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    checkAborted(signal);
    return points;
  }

  /**
   * Recursively loads COPC hierarchy nodes that intersect with the query bounding box.
   */
  private async loadNodesInBounds(
    page: CopcLib.Hierarchy.Page,
    nodes: Map<string, CopcLib.Hierarchy.Node>,
    queryBbox: BoundingBox3D,
    signal?: AbortSignal,
  ): Promise<void> {
    checkAborted(signal);

    const subtree = await CopcLib.Copc.loadHierarchyPage(this.filename, page);
    checkAborted(signal);

    for (const [key, node] of Object.entries(subtree.nodes)) {
      checkAborted(signal);

      if (isNil(node)) continue;

      const nodeBounds = this.getNodeBounds(key);

      if (this.doesBoundingBoxesIntersect(nodeBounds, queryBbox)) {
        nodes.set(key, node);
      }
    }

    for (const [key, childPage] of Object.entries(subtree.pages)) {
      checkAborted(signal);

      if (isNil(childPage)) continue;

      const nodeBounds = this.getNodeBounds(key);
      if (this.doesBoundingBoxesIntersect(nodeBounds, queryBbox)) {
        await this.loadNodesInBounds(childPage, nodes, queryBbox, signal);
      }
    }
  }

  /**
   * Calculates the 3D bounding box for a COPC octree node from its key.
   * COPC uses an octree structure where each level divides space by 2 in each dimension.
   * The key format encodes the node's position in this hierarchy.
   */
  private getNodeBounds(key: string): BoundingBox3D {
    const parts = key.split('-').map(Number);
    const [depth, x = 0, y = 0, z = 0] = parts;

    const cube = this.copcData!.info.cube;
    const [cubeMinX, cubeMinY, cubeMinZ, cubeMaxX, cubeMaxY, cubeMaxZ] = cube;

    const cubeSize = Math.max(
      cubeMaxX - cubeMinX,
      cubeMaxY - cubeMinY,
      cubeMaxZ - cubeMinZ,
    );

    const nodeSize = cubeSize / 2 ** depth;

    const minX = cubeMinX + x * nodeSize;
    const minY = cubeMinY + y * nodeSize;
    const minZ = cubeMinZ + z * nodeSize;

    return {
      minX,
      minY,
      minZ,
      maxX: minX + nodeSize,
      maxY: minY + nodeSize,
      maxZ: minZ + nodeSize,
    };
  }

  /**
   * Checks if two 3D bounding boxes intersect.
   */
  private doesBoundingBoxesIntersect(
    box1: BoundingBox3D,
    box2: BoundingBox3D,
  ): boolean {
    return (
      box1.maxX >= box2.minX &&
      box1.minX <= box2.maxX &&
      box1.maxY >= box2.minY &&
      box1.minY <= box2.maxY &&
      box1.maxZ >= box2.minZ &&
      box1.minZ <= box2.maxZ
    );
  }

  /**
   * Extracts individual points from a COPC point data view.
   * Filters points to those within the specified bounding box.
   * Attempts to extract RGB color and intensity values if available in the point cloud.
   * Falls back gracefully if these attributes are not present.
   */
  private extractPointsFromView(
    view: ViewType,
    queryBbox: BoundingBox3D,
  ): Point[] {
    const points: Point[] = [];

    if (isNil(view) || view.pointCount === 0) {
      return points;
    }

    try {
      const xGetter = view.getter('X');
      const yGetter = view.getter('Y');
      const zGetter = view.getter('Z');

      let rGetter: ((index: number) => number) | null = null;
      let gGetter: ((index: number) => number) | null = null;
      let bGetter: ((index: number) => number) | null = null;
      let intensityGetter: ((index: number) => number) | null = null;

      try {
        rGetter = view.getter('Red');
        gGetter = view.getter('Green');
        bGetter = view.getter('Blue');
        intensityGetter = view.getter('Intensity');
      } catch (error: unknown) {
        // Missing RGB dimensions is expected for some point clouds.
        if (
          error instanceof Error &&
          error.message.includes('No extractor for dimension')
        ) {
          rGetter = null;
          gGetter = null;
          bGetter = null;
          intensityGetter = null;
        }
      }

      for (let i = 0; i < view.pointCount; i++) {
        const x = xGetter(i);
        const y = yGetter(i);
        const z = zGetter(i);

        const { minX, minY, minZ, maxX, maxY, maxZ } = queryBbox;

        if (
          x >= minX &&
          x <= maxX &&
          y >= minY &&
          y <= maxY &&
          z >= minZ &&
          z <= maxZ
        ) {
          const point: Point = { x, y, z };

          if (!isNil(rGetter) && !isNil(gGetter) && !isNil(bGetter)) {
            point.red = rGetter(i);
            point.green = gGetter(i);
            point.blue = bGetter(i);
          }

          if (!isNil(intensityGetter)) {
            point.intensity = intensityGetter(i);
          }

          points.push(point);
        }
      }
    } catch (error: unknown) {
      console.error(
        `Error extracting points from view: ${getErrorMessage(error)}`,
      );
    }

    return points;
  }

  close(): void {
    this.copcData = null;
    this.header = null;
  }
}
